import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  IconButton,
  TextField,
  Chip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  useMediaQuery,
  Paper,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
  Card,
  CardContent,
  Select,
  MenuItem,
  TablePagination,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import PrintIcon from '@material-ui/icons/Print';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import FilterListIcon from '@material-ui/icons/FilterList';
import VisibilityIcon from '@material-ui/icons/Visibility';
import LayersIcon from '@material-ui/icons/Layers';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import useApi from '../../../utils/useApi';
import { formattedCurrency, capitalize } from '../../../utils/utils';
import ProductLabel, { formatParticularitiesText, safeParseParticulars } from './ProductLabel';
import { printProductLabels } from './printLabelEngine';
import { isBarcodeSupported } from './BarcodeSVG';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 12,
    minHeight: '85vh',
    maxHeight: '92vh',
    [theme.breakpoints.down('sm')]: {
      margin: 0,
      borderRadius: 0,
      minHeight: '100%',
      maxHeight: '100%',
    },
  },
  batchCard: {
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
  },
  batchNumberBadge: {
    backgroundColor: '#17497F',
    color: '#ffffff',
    fontWeight: 800,
    fontSize: '0.82rem',
    borderRadius: 16,
    padding: '3px 10px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
  },
  batchPrintedBadge: {
    backgroundColor: '#10b981',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: '0.75rem',
    borderRadius: 12,
    padding: '2px 8px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: '#ffffff',
  },
  tableContainer: {
    maxHeight: '52vh',
    overflowY: 'auto',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
  },
  tableHead: {
    backgroundColor: '#f8fafc',
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  headCell: {
    fontWeight: 700,
    color: '#334155',
    fontSize: '0.82rem',
    whiteSpace: 'nowrap',
    padding: '10px 12px',
  },
  rowSelected: {
    backgroundColor: 'rgba(23, 73, 127, 0.04)',
  },
  previewCard: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 260,
  },
  qtyButton: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: 3,
    minWidth: 28,
    height: 28,
  },
  qtyInput: {
    width: 50,
    margin: '0 4px',
    '& input': {
      textAlign: 'center',
      fontWeight: 'bold',
      fontSize: '0.95rem',
      padding: '4px',
    },
  },
  chipActive: {
    backgroundColor: '#17497F !important',
    color: '#ffffff !important',
    fontWeight: 'bold',
  },
  categoryConfigBox: {
    backgroundColor: '#f0f7ff',
    border: '1px solid #cce3f9',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
  },
  stickyFooter: {
    padding: '14px 24px',
    backgroundColor: '#fbfcfd',
    borderTop: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
}));

const CategoryBulkPrintModal = ({
  open,
  handleClose,
  initialCategory = null,
  initialItems = null,
  referenceType = 'p', // 'p' for products, 's' for services
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Mobile navigation tab: 0 = Itens & Filtros, 1 = Pré-visualização
  const [mobileTab, setMobileTab] = useState(0);

  // Categories API
  const [categoriesLoad, categoriesInfo] = useApi({
    method: 'get',
    url: referenceType === 'p' ? '/products/categories' : '/services/categories',
  });

  // Products by category API
  const [itemsLoad, itemsInfo] = useApi({
    method: 'get',
    url: referenceType === 'p' ? '/products' : '/services',
  });

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [productsData, setProductsData] = useState([]);

  // Selected particularities to appear in the label top box (user can select multiple!)
  const [selectedParticularKeys, setSelectedParticularKeys] = useState([]);

  // Active filters by particularity key: { [key]: value }
  const [activeFilters, setActiveFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // Print settings
  const [printFormat, setPrintFormat] = useState('thermal');
  const [codeType, setCodeType] = useState('barcode'); // 'barcode' | 'qrcode'
  const [showCutLine, setShowCutLine] = useState(true);
  const [showPrice, setShowPrice] = useState(true);
  const [showLateralId, setShowLateralId] = useState(true);

  // Preview index among selected products
  const [previewIndex, setPreviewIndex] = useState(0);

  // Batching states (organized strictly by product)
  const [batchSize, setBatchSize] = useState(100);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [printedBatchIndices, setPrintedBatchIndices] = useState([]);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [showBatchItemsOnly, setShowBatchItemsOnly] = useState(true);

  // A4 has a limit of 30 items per sheet (3 cols x 10 rows). Batches must be multiples of 30 to optimize sheet cost.
  const a4BatchOptions = useMemo(
    () => [
      { value: 30, label: '30 por lote (1 folha)' },
      { value: 60, label: '60 por lote (2 folhas)' },
      { value: 90, label: '90 por lote (3 folhas)' },
      { value: 120, label: '120 por lote (4 folhas - Recomendado)' },
      { value: 150, label: '150 por lote (5 folhas)' },
      { value: 0, label: 'Sem lote (Todas)' },
    ],
    [],
  );

  const thermalBatchOptions = useMemo(
    () => [
      { value: 50, label: '50 por lote' },
      { value: 100, label: '100 por lote (Recomendado)' },
      { value: 200, label: '200 por lote' },
      { value: 500, label: '500 por lote' },
      { value: 0, label: 'Sem lote (Todas)' },
    ],
    [],
  );

  const currentBatchOptions = printFormat === 'a4' ? a4BatchOptions : thermalBatchOptions;

  // Auto-adjust batch size when switching format to optimize sheet costs in A4
  useEffect(() => {
    if (printFormat === 'a4') {
      if (batchSize !== 0 && batchSize % 30 !== 0) {
        setBatchSize(60);
        setCurrentBatchIndex(0);
      }
    } else if (printFormat === 'thermal') {
      if (batchSize !== 0 && (batchSize % 50 !== 0 || [30, 60, 90, 120, 150].includes(batchSize))) {
        setBatchSize(100);
        setCurrentBatchIndex(0);
      }
    }
  }, [printFormat]);

  // Fetch categories when opened
  useEffect(() => {
    if (open) {
      categoriesLoad({ params: { isActive: 1 } });
      setMobileTab(0);
      setPreviewIndex(0);
      setPrintedBatchIndices([]);
      setCurrentBatchIndex(0);
      if (initialItems && initialItems.length > 0) {
        const mapped = initialItems.map((item) => ({
          ...item,
          _parsedParticulars: safeParseParticulars(item.particulars),
          _selected: true,
          _quantity: item._quantity !== undefined ? item._quantity : 1,
        }));
        setProductsData(mapped);
        const keys = Array.from(
          new Set(mapped.flatMap((p) => Object.keys(p._parsedParticulars || {}))),
        );
        setSelectedParticularKeys(keys);
      } else if (initialCategory) {
        setSelectedCategory(initialCategory);
      }
    } else {
      setSelectedCategory(null);
      setProductsData([]);
      setActiveFilters({});
      setSearchTerm('');
      setPrintedBatchIndices([]);
      setCurrentBatchIndex(0);
    }
  }, [open, referenceType, initialCategory, initialItems]);

  // Set initial category if provided or when categories load
  useEffect(() => {
    if (open && categoriesInfo.data && categoriesInfo.data.length > 0) {
      if (initialCategory) {
        const targetId = initialCategory.id !== undefined ? initialCategory.id : initialCategory;
        const found =
          categoriesInfo.data.find(
            (c) =>
              String(c.id) === String(targetId) ||
              (initialCategory.name && c.name === initialCategory.name),
          ) || initialCategory;
        if (!selectedCategory || String(selectedCategory.id) !== String(found.id)) {
          setSelectedCategory(found);
        }
      } else if (!selectedCategory && (!initialItems || initialItems.length === 0)) {
        setSelectedCategory(categoriesInfo.data[0]);
      }
    }
  }, [open, categoriesInfo.data, initialCategory, selectedCategory, initialItems]);

  const selectedCategoryId = selectedCategory?.id;

  // Fetch items when selected category ID changes (avoids redundant calls on object re-reference)
  useEffect(() => {
    if (selectedCategoryId) {
      const params = {
        category: Number(selectedCategoryId),
      };
      if (referenceType === 's') {
        params.isActive = 1;
      }
      itemsLoad({
        params,
      });

      // Parse category's particularities safely
      const categoryParts = safeParseParticulars(selectedCategory?.particulars);
      const keys = Object.keys(categoryParts);
      setSelectedParticularKeys(keys);
      setActiveFilters({});
      setSearchTerm('');
    }
  }, [selectedCategoryId, referenceType]);

  // Map loaded items to table state with selected, quantity, and pre-parsed particulars
  useEffect(() => {
    if (itemsInfo.data) {
      let rawList = [];
      if (Array.isArray(itemsInfo.data)) {
        rawList = itemsInfo.data;
      } else if (Array.isArray(itemsInfo.data?.data)) {
        rawList = itemsInfo.data.data;
      } else if (Array.isArray(itemsInfo.data?.products)) {
        rawList = itemsInfo.data.products;
      }

      const mapped = rawList.map((item) => ({
        ...item,
        _parsedParticulars: safeParseParticulars(item.particulars),
        _selected: true,
        _quantity: 1,
      }));
      setProductsData(mapped);
      setPreviewIndex(0);
    } else if (itemsInfo.error) {
      setProductsData([]);
    }
  }, [itemsInfo.data, itemsInfo.error]);

  // Extract particularities definitions from category safely or fallback to loaded items
  const categoryParticularsDef = useMemo(() => {
    if (selectedCategory?.particulars) {
      return safeParseParticulars(selectedCategory.particulars);
    }
    const combined = {};
    productsData.forEach((p) => {
      const parts = p._parsedParticulars || safeParseParticulars(p.particulars);
      Object.keys(parts).forEach((k) => {
        if (!combined[k]) combined[k] = [];
        if (!combined[k].includes(parts[k])) combined[k].push(parts[k]);
      });
    });
    return combined;
  }, [selectedCategory, productsData]);

  // Handler for filter change
  const handleFilterChange = (key, val) => {
    setActiveFilters((prev) => ({
      ...prev,
      [key]: val === 'ALL' ? undefined : val,
    }));
  };

  // Filter products by all active particularity filters and search term (optimized)
  const filteredProducts = useMemo(() => {
    let result = productsData;

    // Filter by search term
    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      result = result.filter(
        (p) =>
          (p.name && p.name.toLowerCase().includes(term)) ||
          (p.barCode && String(p.barCode).toLowerCase().includes(term)) ||
          String(p.id).includes(term),
      );
    }

    // Filter by active particularities only if filters are explicitly active
    const activeEntries = Object.entries(activeFilters).filter(([, v]) => v && v !== 'ALL');
    if (activeEntries.length > 0) {
      result = result.filter((p) => {
        const pParts = p._parsedParticulars || safeParseParticulars(p.particulars);
        for (const [fKey, fVal] of activeEntries) {
          const matchedKey = Object.keys(pParts).find(
            (k) => k.toLowerCase() === fKey.toLowerCase(),
          );
          if (!matchedKey) return false;
          if (String(pParts[matchedKey]).toLowerCase() !== String(fVal).toLowerCase()) {
            return false;
          }
        }
        return true;
      });
    }

    return result;
  }, [productsData, activeFilters, searchTerm]);

  // Selected products for printing
  const selectedProductsForPrint = useMemo(() => {
    return productsData.filter((p) => p._selected && p._quantity > 0);
  }, [productsData]);

  // Total labels count
  const totalLabelsCount = useMemo(() => {
    return selectedProductsForPrint.reduce(
      (acc, p) => acc + (parseInt(p._quantity, 10) || 0),
      0,
    );
  }, [selectedProductsForPrint]);

  // Toggle item selection
  const handleToggleSelectItem = (id) => {
    setProductsData((prev) =>
      prev.map((p) => (p.id === id ? { ...p, _selected: !p._selected } : p)),
    );
  };

  // Change quantity for item
  const handleItemQuantityChange = (id, newQty) => {
    const safeQty = Math.max(0, parseInt(newQty, 10) || 0);
    setProductsData((prev) =>
      prev.map((p) => (p.id === id ? { ...p, _quantity: safeQty } : p)),
    );
  };

  // Bulk action: Select all / Deselect all
  const handleBulkSelectAll = (select) => {
    if (showBatchItemsOnly && batchSize > 0 && productBatches.length > 1) {
      const currentBatchIds = new Set(displayedTableItems.map((p) => p.id));
      setProductsData((prev) =>
        prev.map((p) => (currentBatchIds.has(p.id) ? { ...p, _selected: select } : p)),
      );
    } else {
      setProductsData((prev) => prev.map((p) => ({ ...p, _selected: select })));
    }
  };

  // Bulk action: Set qty = 1 for selected
  const handleBulkSetQtyOne = () => {
    setProductsData((prev) =>
      prev.map((p) => (p._selected ? { ...p, _quantity: 1 } : p)),
    );
  };

  // Bulk action: Set qty = stock for selected
  const handleBulkSetQtyStock = () => {
    setProductsData((prev) =>
      prev.map((p) => {
        if (!p._selected) return p;
        const stock = parseInt(p.amount, 10);
        return { ...p, _quantity: !Number.isNaN(stock) && stock > 0 ? stock : 1 };
      }),
    );
  };

  // Bulk action: Clear qty
  const handleBulkClearQty = () => {
    setProductsData((prev) =>
      prev.map((p) => (p._selected ? { ...p, _quantity: 0 } : p)),
    );
  };

  // Toggle which particularity key appears in the top box
  const handleToggleParticularKey = (key) => {
    setSelectedParticularKeys((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  };

  // Product Batches: Organized strictly by product over filteredProducts (does not drop unselected items)
  const productBatches = useMemo(() => {
    if (filteredProducts.length === 0) return [];
    if (!batchSize || batchSize <= 0) {
      const checkedLabels = filteredProducts.reduce(
        (acc, p) => acc + (p._selected ? (parseInt(p._quantity, 10) || 0) : 0),
        0,
      );
      return [
        {
          batchIndex: 0,
          totalLabels: checkedLabels,
          products: filteredProducts.map((p) => ({
            product: p,
            quantity: Math.max(0, parseInt(p._quantity, 10) || 0),
          })),
          summary: `${filteredProducts.length} produtos na categoria`,
        },
      ];
    }

    const batches = [];
    let currentBatchItems = [];
    let currentBatchAllocatedSlots = 0;

    filteredProducts.forEach((p) => {
      const configuredQty = Math.max(0, parseInt(p._quantity, 10) || 0);
      const slotWeight = Math.max(1, configuredQty);

      let remainingSlots = slotWeight;
      let partNumber = 1;
      const totalParts = Math.ceil(slotWeight / batchSize);

      while (remainingSlots > 0) {
        const spaceInCurrent = batchSize - currentBatchAllocatedSlots;

        if (spaceInCurrent <= 0) {
          const checkedCount = currentBatchItems.reduce(
            (acc, i) => acc + (i.product._selected ? (parseInt(i.quantity, 10) || 0) : 0),
            0,
          );
          batches.push({
            batchIndex: batches.length,
            totalLabels: checkedCount,
            products: currentBatchItems,
            summary: currentBatchItems
              .filter((i) => i.product._selected && i.quantity > 0)
              .map((i) => `${i.product.name} (${i.quantity} un)`)
              .slice(0, 3)
              .join(', ') + (currentBatchItems.length > 3 ? ` e mais ${currentBatchItems.length - 3}...` : ''),
          });
          currentBatchItems = [];
          currentBatchAllocatedSlots = 0;
        }

        const allocateSlots = Math.min(remainingSlots, batchSize - currentBatchAllocatedSlots);
        const itemQty = configuredQty === 0 ? 0 : allocateSlots;

        currentBatchItems.push({
          product: p,
          quantity: itemQty,
          isPartial: totalParts > 1,
          partInfo: totalParts > 1 ? `Parte ${partNumber} de ${totalParts}` : null,
          totalProductQty: configuredQty,
        });
        currentBatchAllocatedSlots += allocateSlots;
        remainingSlots -= allocateSlots;
        partNumber += 1;
      }
    });

    if (currentBatchItems.length > 0) {
      const checkedCount = currentBatchItems.reduce(
        (acc, i) => acc + (i.product._selected ? (parseInt(i.quantity, 10) || 0) : 0),
        0,
      );
      batches.push({
        batchIndex: batches.length,
        totalLabels: checkedCount,
        products: currentBatchItems,
        summary: currentBatchItems
          .filter((i) => i.product._selected && i.quantity > 0)
          .map((i) => `${i.product.name} (${i.quantity} un)`)
          .slice(0, 3)
          .join(', ') + (currentBatchItems.length > 3 ? ` e mais ${currentBatchItems.length - 3}...` : ''),
      });
    }

    return batches;
  }, [filteredProducts, batchSize]);

  // Ensure batch index stays within valid range
  useEffect(() => {
    if (currentBatchIndex >= productBatches.length && productBatches.length > 0) {
      setCurrentBatchIndex(productBatches.length - 1);
    }
  }, [productBatches.length, currentBatchIndex]);

  const currentBatch = productBatches[currentBatchIndex] || productBatches[0];
  const isCurrentBatchPrinted = printedBatchIndices.includes(currentBatchIndex);

  // Print a specific batch
  const handlePrintBatch = (batchIndexToPrint = currentBatchIndex) => {
    const batch = productBatches[batchIndexToPrint];
    if (!batch || batch.products.length === 0) return;

    const itemsToPrint = batch.products
      .filter((item) => {
        const live =
          productsData.find((p) => String(p.id) === String(item.product.id)) || item.product;
        return live._selected && item.quantity > 0;
      })
      .map((item) => {
        const live =
          productsData.find((p) => String(p.id) === String(item.product.id)) || item.product;
        return {
          product: live,
          quantity: item.quantity,
          selectedParticulars: selectedParticularKeys,
          codeType,
        };
      });

    if (itemsToPrint.length === 0) return;

    printProductLabels(itemsToPrint, {
      format: printFormat,
      codeType,
      showCutLine,
      showLateralId,
      showPrice,
    });

    setPrintedBatchIndices((prev) => Array.from(new Set([...prev, batchIndexToPrint])));

    // Advance to next batch if available
    if (batchIndexToPrint + 1 < productBatches.length) {
      setCurrentBatchIndex(batchIndexToPrint + 1);
    }
  };

  // Print a single product directly
  const handlePrintSingleProduct = (product) => {
    const qty = Math.max(1, parseInt(product._quantity, 10) || 1);
    printProductLabels(
      [
        {
          product,
          quantity: qty,
          selectedParticulars: selectedParticularKeys,
          codeType,
        },
      ],
      {
        format: printFormat,
        codeType,
        showCutLine,
        showLateralId,
        showPrice,
      },
    );
  };

  // Print all items (with warning if high volume)
  const handlePrintAll = (force = false) => {
    if (!force && totalLabelsCount > 200 && batchSize > 0) {
      setWarningDialogOpen(true);
      return;
    }

    const itemsToPrint = selectedProductsForPrint.map((p) => ({
      product: p,
      quantity: p._quantity,
      selectedParticulars: selectedParticularKeys,
      codeType,
    }));

    printProductLabels(itemsToPrint, {
      format: printFormat,
      codeType,
      showCutLine,
      showLateralId,
      showPrice,
    });
  };

  // The product listing: When batching, displays the items of the current batch (checked and unchecked)
  const displayedTableItems = useMemo(() => {
    if (batchSize > 0 && productBatches.length > 0 && currentBatch && showBatchItemsOnly) {
      let list = currentBatch.products.map((bp) => {
        const live = productsData.find((p) => p.id === bp.product.id) || bp.product;
        return {
          ...live,
          _batchQuantity: bp.quantity,
          _partInfo: bp.partInfo,
          _totalProductQty: bp.totalProductQty,
        };
      });

      if (searchTerm && searchTerm.trim()) {
        const term = searchTerm.trim().toLowerCase();
        list = list.filter(
          (p) =>
            (p.name && p.name.toLowerCase().includes(term)) ||
            (p.barCode && String(p.barCode).toLowerCase().includes(term)) ||
            String(p.id).includes(term),
        );
      }
      return list;
    }
    return filteredProducts;
  }, [batchSize, productBatches, currentBatch, showBatchItemsOnly, searchTerm, filteredProducts, productsData]);

  const isBatchView = Boolean(batchSize > 0 && productBatches.length > 0 && showBatchItemsOnly);
  const [allProductsPage, setAllProductsPage] = useState(0);
  const [allProductsRowsPerPage, setAllProductsRowsPerPage] = useState(50);

  // Reset page when filtering or toggling view
  useEffect(() => {
    setAllProductsPage(0);
  }, [searchTerm, activeFilters, selectedCategoryId, showBatchItemsOnly]);

  // Virtual slice: in batch mode, shows batch items (~50-100 max). In all-products mode, paginates 50 per page
  const visibleTableItems = useMemo(() => {
    if (isBatchView) {
      return displayedTableItems;
    }
    const start = allProductsPage * allProductsRowsPerPage;
    return displayedTableItems.slice(start, start + allProductsRowsPerPage);
  }, [isBatchView, displayedTableItems, allProductsPage, allProductsRowsPerPage]);

  const isTableLoading =
    itemsInfo.loading ||
    (Boolean(selectedCategoryId) && !itemsInfo.data && !itemsInfo.error);

  // Current preview item from the displayed items
  const currentPreviewProduct = useMemo(() => {
    if (displayedTableItems.length > 0) {
      return displayedTableItems[previewIndex] || displayedTableItems[0];
    }
    return productsData[0] || null;
  }, [displayedTableItems, previewIndex, productsData]);

  // Check if at least one product in current batch / selection supports 1D barcode
  const anyBarcodeSupported = useMemo(() => {
    const list =
      selectedProductsForPrint.length > 0 ? selectedProductsForPrint : displayedTableItems;
    if (list.length === 0) return true;
    return list.some((p) => {
      const val = String(p?.barCode || p?.id || '').trim();
      return isBarcodeSupported(val);
    });
  }, [selectedProductsForPrint, displayedTableItems]);

  // Only force QR Code if NO product in the list supports 1D Barcode
  useEffect(() => {
    if (!anyBarcodeSupported) {
      setCodeType('qrcode');
    }
  }, [anyBarcodeSupported]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      classes={{ paper: classes.dialogPaper }}
    >
      {/* Header */}
      <DialogTitle disableTypography className={classes.header}>
        <Box display="flex" alignItems="center" gap={1}>
          <PrintIcon style={{ color: '#17497F', marginRight: 10 }} />
          <Box>
            <Typography variant="h6" style={{ fontWeight: 700, fontSize: '1.15rem' }}>
              Imprimir etiquetas
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Configure particularidades, formato e design da etiqueta
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Tabs for Mobile */}
      {isMobile && (
        <Box borderBottom={1} borderColor="divider" bgcolor="#f8fafc">
          <Tabs
            value={mobileTab}
            onChange={(e, val) => setMobileTab(val)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label={`1. Produtos (${selectedProductsForPrint.length})`} />
            <Tab label={`2. Prévia & Formato (${totalLabelsCount})`} />
          </Tabs>
        </Box>
      )}

      <DialogContent style={{ padding: isMobile ? '12px' : '20px' }}>
        {/* Painel Superior: Seleção de Categoria e Particularidades */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Autocomplete
              options={Array.isArray(categoriesInfo.data) ? categoriesInfo.data : []}
              getOptionLabel={(option) => option.name || ''}
              value={selectedCategory || null}
              onChange={(event, newValue) => setSelectedCategory(newValue)}
              getOptionSelected={(option, value) =>
                Boolean(option && value && String(option.id) === String(value.id))
              }
              loading={categoriesInfo.loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Selecione a Categoria"
                  variant="outlined"
                  size="small"
                  placeholder="Buscar categoria..."
                />
              )}
            />
          </Grid>

          {/* Destaque das Particularidades configuradas na categoria */}
          <Grid item xs={12} md={7}>
            <Box className={classes.categoryConfigBox}>
              <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#17497F', marginBottom: 4 }}>
                Particularidades no Topo da Etiqueta:
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
                Escolha quais características aparecem dentro da caixa superior (você pode selecionar mais de uma):
              </Typography>

              <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                {Object.keys(categoryParticularsDef).length > 0 ? (
                  Object.keys(categoryParticularsDef).map((key) => {
                    const isSelected = selectedParticularKeys.some(
                      (sk) => sk.toLowerCase() === key.toLowerCase(),
                    );
                    return (
                      <Chip
                        key={key}
                        label={`${capitalize(key)} (ex: ${key.toUpperCase()}: ...)`}
                        onClick={() => handleToggleParticularKey(key)}
                        clickable
                        color={isSelected ? 'primary' : 'default'}
                        className={isSelected ? classes.chipActive : undefined}
                        style={{ margin: 3 }}
                      />
                    );
                  })
                ) : (
                  <Typography variant="caption" color="textSecondary">
                    Esta categoria não possui particularidades fixas definidas.
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Filtros rápidos por particularidade (ex: Estampada, Tamanho...) */}
        {Object.keys(categoryParticularsDef).length > 0 && (
          <Box mb={2} mt={1} p={1.5} bgcolor="#f8fafc" borderRadius={8} border="1px solid #e2e8f0">
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
              <Box display="flex" alignItems="center">
                <FilterListIcon fontSize="small" style={{ color: '#64748b', marginRight: 6 }} />
                <Typography variant="caption" style={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem' }}>
                  Filtros de Particularidades:
                </Typography>
              </Box>

              <TextField
                size="small"
                variant="outlined"
                placeholder="Buscar produto por nome ou código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: isMobile ? '100%' : 260 }}
              />
            </Box>

            {Object.entries(categoryParticularsDef).map(([pKey, pVals]) => {
              const valsList = Array.isArray(pVals) ? pVals : [];
              if (valsList.length === 0) return null;
              const currentVal = activeFilters[pKey] || 'ALL';

              return (
                <Box key={pKey} display="flex" alignItems="center" flexWrap="wrap" gap={0.5} my={0.5}>
                  <Typography variant="caption" style={{ fontWeight: 700, color: '#475569', minWidth: 80 }}>
                    {capitalize(pKey)}:
                  </Typography>
                  <Chip
                    size="small"
                    label="Todos"
                    onClick={() => handleFilterChange(pKey, 'ALL')}
                    color={currentVal === 'ALL' ? 'primary' : 'default'}
                    className={currentVal === 'ALL' ? classes.chipActive : undefined}
                    style={{ margin: 2 }}
                  />
                  {valsList.map((val) => {
                    const isSelected = String(currentVal).toLowerCase() === String(val).toLowerCase();
                    return (
                      <Chip
                        key={val}
                        size="small"
                        label={val}
                        onClick={() => handleFilterChange(pKey, val)}
                        color={isSelected ? 'primary' : 'default'}
                        className={isSelected ? classes.chipActive : undefined}
                        style={{ margin: 2 }}
                      />
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Layout Principal: Grid no PC / Abas no Mobile */}
        <Grid container spacing={3}>
          {/* Coluna da Esquerda: Tabela de Produtos e Ações em Lote */}
          {(!isMobile || mobileTab === 0) && (
            <Grid item xs={12} md={7}>
              {/* Banner de Lotes por Produto */}
              {productBatches.length > 1 && batchSize > 0 && (
                <Paper elevation={0} className={classes.batchCard}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                      <span className={classes.batchNumberBadge}>
                        <LayersIcon style={{ fontSize: 16 }} />
                        Lote {currentBatchIndex + 1} de {productBatches.length}
                      </span>

                      <Chip
                        size="small"
                        label={`${currentBatch?.totalLabels || 0} etiquetas${printFormat === 'a4' ? ` (${Math.ceil((currentBatch?.totalLabels || 0) / 30)} f.)` : ''}`}
                        style={{ fontWeight: 700, backgroundColor: '#dbeafe', color: '#1e40af' }}
                      />

                      {isCurrentBatchPrinted && (
                        <span className={classes.batchPrintedBadge}>
                          <CheckCircleIcon style={{ fontSize: 13 }} />
                          Impresso
                        </span>
                      )}
                    </Box>

                    {/* Controles de Navegação e Tamanho do Lote */}
                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                      <Select
                        value={currentBatchIndex}
                        onChange={(e) => {
                          setCurrentBatchIndex(Number(e.target.value));
                          setPreviewIndex(0);
                        }}
                        variant="outlined"
                        style={{ height: 30, fontSize: '0.82rem', minWidth: 140 }}
                      >
                        {productBatches.map((b, i) => (
                          <MenuItem key={i} value={i}>
                            Lote {i + 1} ({b.totalLabels} un{printFormat === 'a4' ? ` • ${Math.ceil(b.totalLabels / 30)} f.` : ''}){printedBatchIndices.includes(i) ? ' ✓' : ''}
                          </MenuItem>
                        ))}
                      </Select>

                      <IconButton
                        size="small"
                        onClick={() => {
                          setCurrentBatchIndex((prev) => Math.max(0, prev - 1));
                          setPreviewIndex(0);
                        }}
                        disabled={currentBatchIndex <= 0}
                        title="Lote Anterior"
                      >
                        <NavigateBeforeIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setCurrentBatchIndex((prev) =>
                            Math.min(productBatches.length - 1, prev + 1),
                          );
                          setPreviewIndex(0);
                        }}
                        disabled={currentBatchIndex >= productBatches.length - 1}
                        title="Próximo Lote"
                      >
                        <NavigateNextIcon />
                      </IconButton>

                      <Divider orientation="vertical" flexItem style={{ height: 20, margin: 'auto 4px' }} />

                      <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                        Tamanho:
                      </Typography>
                      <Select
                        value={batchSize}
                        onChange={(e) => {
                          setBatchSize(Number(e.target.value));
                          setCurrentBatchIndex(0);
                          setPreviewIndex(0);
                        }}
                        variant="outlined"
                        style={{ height: 30, fontSize: '0.82rem' }}
                      >
                        {currentBatchOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  </Box>

                  {/* Resumo dos produtos no lote atual */}
                  <Box mt={1} pt={1} borderTop="1px dashed #bfdbfe" display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<PrintIcon />}
                      onClick={() => handlePrintBatch(currentBatchIndex)}
                      style={{ fontWeight: 700, textTransform: 'none', fontSize: '0.8rem' }}
                    >
                      Imprimir Lote {currentBatchIndex + 1} ({currentBatch?.totalLabels || 0} un{printFormat === 'a4' ? ` • ${Math.ceil((currentBatch?.totalLabels || 0) / 30)} folha(s)` : ''})
                    </Button>
                  </Box>
                </Paper>
              )}



              {/* Barra de Ações em Lote */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={1}
                mb={1.5}
                p={1}
                bgcolor="#f8fafc"
                borderRadius={6}
                border="1px solid #e2e8f0"
              >
                <Box display="flex" alignItems="center">
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={
                      displayedTableItems.length > 0 &&
                      displayedTableItems.every((p) => p._selected)
                    }
                    indeterminate={
                      displayedTableItems.some((p) => p._selected) &&
                      !displayedTableItems.every((p) => p._selected)
                    }
                    onChange={(e) => handleBulkSelectAll(e.target.checked)}
                  />
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {displayedTableItems.filter((p) => p._selected).length} de {displayedTableItems.length} selecionados
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
                  <Button
                    size="small"
                    variant="outlined"
                    style={{ textTransform: 'none', fontSize: '0.75rem' }}
                    onClick={handleBulkSetQtyOne}
                  >
                    Qtd = 1
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    style={{ textTransform: 'none', fontSize: '0.75rem' }}
                    onClick={handleBulkSetQtyStock}
                  >
                    Qtd = Estoque
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    style={{ textTransform: 'none', fontSize: '0.75rem' }}
                    onClick={handleBulkClearQty}
                  >
                    Zerar
                  </Button>
                </Box>
              </Box>

              {/* Tabela de Produtos */}
              {isTableLoading ? (
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" p={6}>
                  <CircularProgress size={36} />
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
                    Carregando produtos da categoria...
                  </Typography>
                </Box>
              ) : itemsInfo.error ? (
                <Box p={4} textAlign="center">
                  <Typography color="error" style={{ marginBottom: 12, fontWeight: 600 }}>
                    Erro ao carregar os produtos desta categoria.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      if (selectedCategory?.id) {
                        const params = { category: Number(selectedCategory.id) };
                        if (referenceType === 's') params.isActive = 1;
                        itemsLoad({ params });
                      }
                    }}
                  >
                    Tentar Novamente
                  </Button>
                </Box>
              ) : displayedTableItems.length === 0 ? (
                <Box p={4} textAlign="center">
                  <Typography color="textSecondary">
                    {productsData.length > 0
                      ? 'Nenhum produto corresponde aos filtros de particularidades ou busca.'
                      : 'Nenhum produto cadastrado nesta categoria.'}
                  </Typography>
                  {productsData.length > 0 && (
                    <Button
                      size="small"
                      color="primary"
                      variant="text"
                      onClick={() => {
                        setActiveFilters({});
                        setSearchTerm('');
                      }}
                      style={{ marginTop: 8 }}
                    >
                      Limpar Filtros
                    </Button>
                  )}
                </Box>
              ) : (
                <>
                  {isMobile ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={1}
                      style={{
                        maxHeight: '52vh',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        padding: '4px 2px',
                      }}
                    >
                      {visibleTableItems.map((item, idx) => {
                        const partsText = formatParticularitiesText(
                          item._parsedParticulars || item.particulars,
                          selectedParticularKeys,
                        );

                        const currentQty =
                          item._batchQuantity !== undefined ? item._batchQuantity : item._quantity;

                        return (
                          <Paper
                            key={`${item.id}-${idx}`}
                            elevation={0}
                            style={{
                              padding: '10px 12px',
                              borderRadius: 8,
                              border: `1.5px solid ${item._selected ? '#17497F' : '#e2e8f0'}`,
                              backgroundColor: item._selected ? 'rgba(23, 73, 127, 0.04)' : '#ffffff',
                              boxSizing: 'border-box',
                              width: '100%',
                            }}
                          >
                            {/* Linha superior: Checkbox + Nome + Preço */}
                            <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                              <Box display="flex" alignItems="flex-start" gap={0.5} style={{ flex: 1, minWidth: 0 }}>
                                <Checkbox
                                  size="small"
                                  color="primary"
                                  checked={Boolean(item._selected)}
                                  onChange={() => handleToggleSelectItem(item.id)}
                                  style={{ padding: 2, marginTop: -2 }}
                                />
                                <Box style={{ minWidth: 0, flex: 1 }}>
                                  <Typography
                                    variant="body2"
                                    style={{
                                      fontWeight: 700,
                                      fontSize: '0.88rem',
                                      lineHeight: 1.25,
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {item.name}
                                  </Typography>
                                  <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" mt={0.5}>
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                      style={{
                                        fontFamily: "'Courier New', Courier, monospace",
                                        fontSize: '0.72rem',
                                        wordBreak: 'break-all',
                                      }}
                                    >
                                      Cód: {item.barCode || `#${item.id}`}
                                    </Typography>
                                    {item._partInfo && (
                                      <Chip
                                        size="small"
                                        label={item._partInfo}
                                        style={{
                                          height: 18,
                                          fontSize: '0.65rem',
                                          backgroundColor: '#fef3c7',
                                          color: '#92400e',
                                          fontWeight: 700,
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              </Box>

                              <Typography
                                variant="body2"
                                style={{
                                  fontWeight: 800,
                                  color: '#111827',
                                  fontSize: '0.92rem',
                                  whiteSpace: 'nowrap',
                                  paddingLeft: 4,
                                }}
                              >
                                {formattedCurrency(item.value)}
                              </Typography>
                            </Box>

                            {/* Linha inferior: Particularidade + Estoque + Controle de Quantidade */}
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              flexWrap="wrap"
                              gap={1}
                              mt={1}
                              pt={0.75}
                              style={{ borderTop: '1px dashed #e2e8f0' }}
                            >
                              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                {partsText && (
                                  <Chip
                                    size="small"
                                    label={partsText}
                                    variant="outlined"
                                    style={{
                                      fontWeight: 700,
                                      borderColor: '#17497F',
                                      color: '#17497F',
                                      height: 20,
                                      fontSize: '0.68rem',
                                    }}
                                  />
                                )}
                                <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.75rem' }}>
                                  Estoque: <b>{item.amount || 0}</b> un
                                </Typography>
                              </Box>

                              {/* Stepper de quantidade e botão imprimir */}
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography variant="caption" style={{ fontWeight: 600, color: '#64748b', marginRight: 2 }}>
                                  {showBatchItemsOnly && batchSize > 0 ? 'No Lote:' : 'Etiquetas:'}
                                </Typography>
                                <IconButton
                                  size="small"
                                  className={classes.qtyButton}
                                  onClick={() => handleItemQuantityChange(item.id, currentQty - 1)}
                                  disabled={currentQty <= 0}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                                <TextField
                                  type="number"
                                  value={currentQty}
                                  onChange={(e) =>
                                    handleItemQuantityChange(item.id, e.target.value)
                                  }
                                  className={classes.qtyInput}
                                  inputProps={{ min: 0 }}
                                  size="small"
                                  variant="outlined"
                                />
                                <IconButton
                                  size="small"
                                  className={classes.qtyButton}
                                  onClick={() => handleItemQuantityChange(item.id, currentQty + 1)}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                                <Tooltip title={`Imprimir diretamente ${currentQty} etiqueta(s)`}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handlePrintSingleProduct(item)}
                                      disabled={currentQty <= 0}
                                      style={{ marginLeft: 2 }}
                                    >
                                      <PrintIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  ) : (
                    <TableContainer className={classes.tableContainer}>
                      <Table size="small" stickyHeader>
                        <TableHead className={classes.tableHead}>
                          <TableRow>
                            <TableCell padding="checkbox" className={classes.headCell} />
                            <TableCell className={classes.headCell}>Produto</TableCell>
                            <TableCell className={classes.headCell}>Particularidade</TableCell>
                            <TableCell className={classes.headCell} align="right">Preço</TableCell>
                            <TableCell className={classes.headCell} align="center">Estoque</TableCell>
                            <TableCell className={classes.headCell} align="center">
                              {showBatchItemsOnly && batchSize > 0
                                ? `Qtd. no Lote ${currentBatchIndex + 1}`
                                : 'Qtd. Etiquetas'}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {visibleTableItems.map((item, idx) => {
                            const partsText = formatParticularitiesText(
                              item._parsedParticulars || item.particulars,
                              selectedParticularKeys,
                            );

                            const currentQty =
                              item._batchQuantity !== undefined ? item._batchQuantity : item._quantity;

                            return (
                              <TableRow
                                key={`${item.id}-${idx}`}
                                hover
                                className={item._selected ? classes.rowSelected : undefined}
                              >
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    size="small"
                                    color="primary"
                                    checked={Boolean(item._selected)}
                                    onChange={() => handleToggleSelectItem(item.id)}
                                  />
                                </TableCell>

                                <TableCell style={{ maxWidth: 220 }}>
                                  <Typography variant="body2" style={{ fontWeight: 600 }} noWrap>
                                    {item.name}
                                  </Typography>
                                  <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                                    <Typography variant="caption" color="textSecondary">
                                      Cód: {item.barCode || `#${item.id}`}
                                    </Typography>
                                    {item._partInfo && (
                                      <Chip
                                        size="small"
                                        label={item._partInfo}
                                        style={{
                                          height: 18,
                                          fontSize: '0.68rem',
                                          backgroundColor: '#fef3c7',
                                          color: '#92400e',
                                          fontWeight: 700,
                                        }}
                                      />
                                    )}
                                  </Box>
                                </TableCell>

                                <TableCell>
                                  {partsText ? (
                                    <Chip
                                      size="small"
                                      label={partsText}
                                      variant="outlined"
                                      style={{ fontWeight: 700, borderColor: '#17497F', color: '#17497F' }}
                                    />
                                  ) : (
                                    <Typography variant="caption" color="textSecondary">-</Typography>
                                  )}
                                </TableCell>

                                <TableCell align="right" style={{ fontWeight: 700 }}>
                                  {formattedCurrency(item.value)}
                                </TableCell>

                                <TableCell align="center">
                                  {item.amount || 0} un
                                </TableCell>

                                <TableCell align="center">
                                  <Box display="flex" alignItems="center" justifyContent="center">
                                    <IconButton
                                      size="small"
                                      className={classes.qtyButton}
                                      onClick={() => handleItemQuantityChange(item.id, currentQty - 1)}
                                      disabled={currentQty <= 0}
                                    >
                                      <RemoveIcon fontSize="small" />
                                    </IconButton>
                                    <TextField
                                      type="number"
                                      value={currentQty}
                                      onChange={(e) => handleItemQuantityChange(item.id, e.target.value)}
                                      className={classes.qtyInput}
                                      inputProps={{ min: 0 }}
                                      size="small"
                                      variant="outlined"
                                    />
                                    <IconButton
                                      size="small"
                                      className={classes.qtyButton}
                                      onClick={() => handleItemQuantityChange(item.id, currentQty + 1)}
                                    >
                                      <AddIcon fontSize="small" />
                                    </IconButton>
                                    <Tooltip title={`Imprimir diretamente ${currentQty} etiqueta(s) deste produto`}>
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => handlePrintSingleProduct(item)}
                                          disabled={currentQty <= 0}
                                          style={{ marginLeft: 6 }}
                                        >
                                          <PrintIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {!isBatchView && displayedTableItems.length > 0 && (
                    <TablePagination
                      component="div"
                      count={displayedTableItems.length}
                      page={allProductsPage}
                      onChangePage={(e, newPage) => setAllProductsPage(newPage)}
                      rowsPerPage={allProductsRowsPerPage}
                      onChangeRowsPerPage={(e) => {
                        setAllProductsRowsPerPage(parseInt(e.target.value, 10));
                        setAllProductsPage(0);
                      }}
                      rowsPerPageOptions={[25, 50, 100]}
                      labelRowsPerPage="Itens por página:"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                  )}
                </>
              )}
            </Grid>
          )}

          {/* Coluna da Direita: Painel de Prévia e Configurações */}
          {(!isMobile || mobileTab === 1) && (
            <Grid item xs={12} md={5}>
              <Box display="flex" flexDirection="column" gap={2}>
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                  Pré-visualização da Etiqueta
                </Typography>

                {currentPreviewProduct ? (
                  <>
                    <Paper className={classes.previewCard} elevation={0}>
                      <ProductLabel
                        product={currentPreviewProduct}
                        selectedParticulars={selectedParticularKeys}
                        codeType={codeType}
                        showCutLine={showCutLine}
                        showPrice={showPrice}
                        showLateralId={showLateralId}
                        scale={1}
                      />
                    </Paper>

                    {/* Navegação da Prévia se houver vários produtos */}
                    {selectedProductsForPrint.length > 1 && (
                      <Box display="flex" alignItems="center" justifyContent="space-between" px={1}>
                        <IconButton
                          size="small"
                          disabled={previewIndex <= 0}
                          onClick={() => setPreviewIndex((i) => Math.max(0, i - 1))}
                        >
                          <NavigateBeforeIcon />
                        </IconButton>
                        <Typography variant="caption" style={{ fontWeight: 600 }}>
                          Item {previewIndex + 1} de {selectedProductsForPrint.length}: {currentPreviewProduct.name}
                        </Typography>
                        <IconButton
                          size="small"
                          disabled={previewIndex >= selectedProductsForPrint.length - 1}
                          onClick={() => setPreviewIndex((i) => Math.min(selectedProductsForPrint.length - 1, i + 1))}
                        >
                          <NavigateNextIcon />
                        </IconButton>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box p={4} textAlign="center" bgcolor="#f8fafc" borderRadius={8} border="1px solid #e2e8f0">
                    <Typography variant="body2" color="textSecondary">
                      Selecione ao menos um produto com quantidade maior que 0 para pré-visualizar.
                    </Typography>
                  </Box>
                )}

                {/* Opções de Impressão */}
                <Paper style={{ padding: '16px', borderRadius: 8, border: '1px solid #e2e8f0' }} elevation={0}>
                  <FormControl component="fieldset" style={{ marginBottom: 12 }}>
                    <FormLabel component="legend" style={{ fontSize: '0.8rem', fontWeight: 700 }}>
                      Padrão do Código:
                    </FormLabel>
                    <RadioGroup
                      row
                      value={codeType}
                      onChange={(e) => setCodeType(e.target.value)}
                    >
                      <FormControlLabel
                        value="barcode"
                        disabled={!anyBarcodeSupported}
                        control={<Radio size="small" color="primary" />}
                        label={
                          <Typography
                            variant="body2"
                            style={{ color: !anyBarcodeSupported ? '#9ca3af' : undefined }}
                          >
                            Código de Barras
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="qrcode"
                        control={<Radio size="small" color="primary" />}
                        label={<Typography variant="body2">QR Code</Typography>}
                      />
                    </RadioGroup>
                    {codeType === 'barcode' && currentPreviewProduct && !isBarcodeSupported(String(currentPreviewProduct?.barCode || currentPreviewProduct?.id || '')) && (
                      <Typography variant="caption" style={{ color: '#64748b', display: 'block', marginTop: 2, fontSize: '0.72rem' }}>
                        * O produto em prévia usa QR Code por não possuir código compatível. Itens com código de barras válido serão gerados com código de barras.
                      </Typography>
                    )}
                  </FormControl>

                  <FormControl component="fieldset" style={{ marginBottom: 12 }}>
                    <FormLabel component="legend" style={{ fontSize: '0.8rem', fontWeight: 700 }}>
                      Formato de Saída:
                    </FormLabel>
                    <RadioGroup
                      row
                      value={printFormat}
                      onChange={(e) => setPrintFormat(e.target.value)}
                    >
                      <FormControlLabel
                        value="thermal"
                        control={<Radio size="small" color="primary" />}
                        label={<Typography variant="body2">Rolo Térmico (Bobina)</Typography>}
                      />
                      <FormControlLabel
                        value="a4"
                        control={<Radio size="small" color="primary" />}
                        label={<Typography variant="body2">Folha A4 (Grade 30 un)</Typography>}
                      />
                    </RadioGroup>
                  </FormControl>

                  <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showCutLine}
                          onChange={(e) => setShowCutLine(e.target.checked)}
                          size="small"
                          color="primary"
                        />
                      }
                      label={<Typography variant="body2">Picote ✂</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showPrice}
                          onChange={(e) => setShowPrice(e.target.checked)}
                          size="small"
                          color="primary"
                        />
                      }
                      label={<Typography variant="body2">Preço</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showLateralId}
                          onChange={(e) => setShowLateralId(e.target.checked)}
                          size="small"
                          color="primary"
                        />
                      }
                      label={<Typography variant="body2">ID Lateral</Typography>}
                    />
                  </Box>
                </Paper>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      {/* Rodapé Fixo */}
      <div className={classes.stickyFooter}>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography
            variant="subtitle1"
            style={{ fontWeight: 800, color: '#17497F', fontSize: isMobile ? '0.95rem' : '1.1rem' }}
          >
            Total: {totalLabelsCount} {totalLabelsCount === 1 ? 'etiqueta' : 'etiquetas'}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
            ({selectedProductsForPrint.length}{' '}
            {selectedProductsForPrint.length === 1
              ? 'item distinto selecionado'
              : 'itens distintos selecionados'})
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          {isMobile && mobileTab === 1 ? (
            <Button onClick={() => setMobileTab(0)} color="default">
              Voltar aos Produtos
            </Button>
          ) : (
            <Button onClick={handleClose} color="default">
              Cancelar
            </Button>
          )}

          {isMobile && mobileTab === 0 ? (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setMobileTab(1)}
              disableElevation
              endIcon={<VisibilityIcon />}
              style={{ fontWeight: 700 }}
            >
              Ver Prévia ({totalLabelsCount})
            </Button>
          ) : productBatches.length > 1 && batchSize > 0 ? (
            <>
              {totalLabelsCount > 0 && (
                <Button
                  variant="text"
                  color="default"
                  onClick={() => handlePrintAll(false)}
                  style={{ textTransform: 'none', fontSize: '0.82rem', color: '#64748b' }}
                >
                  Imprimir Todas ({totalLabelsCount}{printFormat === 'a4' ? ` un • ${Math.ceil(totalLabelsCount / 30)} folha(s)` : ''})
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={() => handlePrintBatch(currentBatchIndex)}
                disabled={totalLabelsCount <= 0 || !currentBatch}
                startIcon={<PrintIcon />}
                disableElevation
                style={{ fontWeight: 700 }}
              >
                Imprimir Lote {currentBatchIndex + 1} de {productBatches.length} ({currentBatch?.totalLabels || 0} un{printFormat === 'a4' ? ` • ${Math.ceil((currentBatch?.totalLabels || 0) / 30)} folha(s)` : ''})
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={() => handlePrintAll(false)}
              disabled={totalLabelsCount <= 0}
              startIcon={<PrintIcon />}
              disableElevation
              style={{ fontWeight: 700 }}
            >
              Imprimir {totalLabelsCount} Etiquetas{printFormat === 'a4' ? ` (${Math.ceil(totalLabelsCount / 30)} folha(s))` : ''}
            </Button>
          )}
        </Box>
      </div>

      {/* Diálogo de confirmação para impressão direta de alto volume */}
      <Dialog
        open={warningDialogOpen}
        onClose={() => setWarningDialogOpen(false)}
        maxWidth="xs"
      >
        <DialogTitle style={{ fontWeight: 700 }}>
          Alto Volume de Etiquetas
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: 12 }}>
            Você está prestes a gerar <strong>{totalLabelsCount} etiquetas</strong> de uma só vez no navegador.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            O Chrome pode levar vários minutos para compilar milhares de páginas em memória antes de abrir o diálogo. Recomendamos utilizar os <strong>lotes de {batchSize} etiquetas</strong> organizados por produto para abertura instantânea.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '12px 20px' }}>
          <Button onClick={() => setWarningDialogOpen(false)} color="default">
            Usar Lotes (Recomendado)
          </Button>
          <Button
            onClick={() => {
              setWarningDialogOpen(false);
              handlePrintAll(true);
            }}
            color="primary"
            variant="contained"
            disableElevation
          >
            Imprimir Todas Mesmo Assim
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CategoryBulkPrintModal;
